import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import RichTextEditor from "./RichTextEditor";
import ForeignKeyPicker from "./ForeignKeyPicker";

const inputClass =
    "mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11";

/**
 * Renders a single form field per SCRUD column spec.
 * Column shape: { name, label, inputType, nullable, length, isPrimaryKey, fk? }
 */
export default function FieldRenderer({ field, value, onChange, disabled = false }) {
    const setVal = (v) => onChange(field.name, v);

    // Foreign-key columns take precedence — no matter their raw inputType.
    if (field.fk) {
        return (
            <div>
                <Label className="text-navy-700 text-xs uppercase tracking-widest">
                    {field.label}
                    {!field.nullable && <span className="text-gold-600 ml-1">*</span>}
                    <span className="ml-2 normal-case tracking-normal text-[10px] text-gold-600">
                        · linked to {field.fk.table}
                    </span>
                </Label>
                <ForeignKeyPicker
                    fk={field.fk}
                    value={value}
                    onChange={setVal}
                    disabled={disabled}
                />
            </div>
        );
    }

    if (field.inputType === "wysiwyg") {
        return (
            <div className="space-y-2">
                <Label className="text-navy-700 text-xs uppercase tracking-widest">
                    {field.label}
                    {!field.nullable && <span className="text-gold-600 ml-1">*</span>}
                </Label>
                <RichTextEditor
                    value={value || ""}
                    onChange={setVal}
                    placeholder={field.label}
                    readOnly={disabled}
                />
            </div>
        );
    }

    if (field.inputType === "checkbox") {
        return (
            <div className="flex items-center gap-3 pt-6">
                <Checkbox
                    id={field.name}
                    data-testid={`field-${field.name}`}
                    checked={Boolean(value)}
                    onCheckedChange={setVal}
                    disabled={disabled}
                    className="data-[state=checked]:bg-navy-700 data-[state=checked]:border-navy-700"
                />
                <Label
                    htmlFor={field.name}
                    className="text-navy-700 text-sm cursor-pointer"
                >
                    {field.label}
                </Label>
            </div>
        );
    }

    if (field.inputType === "textarea") {
        return (
            <div>
                <Label className="text-navy-700 text-xs uppercase tracking-widest">
                    {field.label}
                    {!field.nullable && <span className="text-gold-600 ml-1">*</span>}
                </Label>
                <Textarea
                    data-testid={`field-${field.name}`}
                    rows={4}
                    value={value ?? ""}
                    disabled={disabled}
                    onChange={(e) => setVal(e.target.value)}
                    className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500"
                />
            </div>
        );
    }

    const htmlType =
        field.inputType === "number"
            ? "number"
            : field.inputType === "date"
              ? "date"
              : field.inputType === "datetime-local"
                ? "datetime-local"
                : field.inputType === "time"
                  ? "time"
                  : "text";

    return (
        <div>
            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                {field.label}
                {!field.nullable && <span className="text-gold-600 ml-1">*</span>}
            </Label>
            <Input
                id={field.name}
                data-testid={`field-${field.name}`}
                type={htmlType}
                value={value ?? ""}
                disabled={disabled}
                onChange={(e) => setVal(e.target.value)}
                className={inputClass}
            />
        </div>
    );
}
